(function (root, factory) {
  'use strict';

  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
    return;
  }

  var api = root.__vmaxFlashSale || factory();
  root.__vmaxFlashSale = api;
  api.boot(root.document, root);
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  var formatterCache = new Map();
  var sectionInstances = new WeakMap();
  var bootedDocuments = new WeakSet();
  var responsivePlacements = new Set();

  function getFormatter(timeZone) {
    if (!formatterCache.has(timeZone)) {
      formatterCache.set(
        timeZone,
        new Intl.DateTimeFormat('en-CA', {
          timeZone: timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        })
      );
    }

    return formatterCache.get(timeZone);
  }

  function getZonedParts(timestampMs, timeZone) {
    var values = {};

    getFormatter(timeZone)
      .formatToParts(new Date(timestampMs))
      .forEach(function (part) {
        if (part.type !== 'literal') {
          values[part.type] = Number(part.value);
        }
      });

    return {
      year: values.year,
      month: values.month,
      day: values.day,
      hour: values.hour,
      minute: values.minute,
      second: values.second
    };
  }

  function getOffsetMs(timestampMs, timeZone) {
    var parts = getZonedParts(timestampMs, timeZone);
    var asUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second
    );

    return asUtc - Math.floor(timestampMs / 1000) * 1000;
  }

  function sameLocalMinute(parts, localDateTime) {
    return (
      parts.year === localDateTime.year &&
      parts.month === localDateTime.month &&
      parts.day === localDateTime.day &&
      parts.hour === localDateTime.hour &&
      parts.minute === localDateTime.minute
    );
  }

  function localDateTimeToUtc(localDateTime, timeZone) {
    var wallClockMs = Date.UTC(
      localDateTime.year,
      localDateTime.month - 1,
      localDateTime.day,
      localDateTime.hour,
      localDateTime.minute,
      0
    );
    var offsets = [
      getOffsetMs(wallClockMs - 86400000, timeZone),
      getOffsetMs(wallClockMs, timeZone),
      getOffsetMs(wallClockMs + 86400000, timeZone)
    ];
    var candidates = [];

    offsets.forEach(function (offset) {
      var candidate = wallClockMs - offset;

      if (
        sameLocalMinute(getZonedParts(candidate, timeZone), localDateTime) &&
        candidates.indexOf(candidate) === -1
      ) {
        candidates.push(candidate);
      }
    });

    candidates.sort(function (left, right) {
      return left - right;
    });

    if (candidates.length) {
      return candidates[0];
    }

    var requestedMinute = localDateTime.hour * 60 + localDateTime.minute;
    var searchStart = wallClockMs - 18 * 60 * 60 * 1000;
    var searchEnd = wallClockMs + 18 * 60 * 60 * 1000;

    for (
      var timestampMs = searchStart;
      timestampMs <= searchEnd;
      timestampMs += 60 * 1000
    ) {
      var parts = getZonedParts(timestampMs, timeZone);
      var localMinute = parts.hour * 60 + parts.minute;

      if (
        parts.year === localDateTime.year &&
        parts.month === localDateTime.month &&
        parts.day === localDateTime.day &&
        localMinute >= requestedMinute
      ) {
        return timestampMs;
      }
    }

    throw new RangeError('Unable to resolve the configured local start time');
  }

  function addLocalDays(localDate, days) {
    var value = new Date(
      Date.UTC(localDate.year, localDate.month - 1, localDate.day + days)
    );

    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate()
    };
  }

  function validateDuration(durationMinutes) {
    var value = Number(durationMinutes);

    if (!Number.isFinite(value) || value <= 0 || value >= 7 * 24 * 60) {
      throw new RangeError(
        'durationMinutes must be greater than 0 and less than 10080'
      );
    }

    return value;
  }

  function validateSchedule(schedule) {
    var weekday = Number(schedule.weekday);

    if (!Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
      throw new RangeError('weekday must be an integer from 0 to 6');
    }

    if (!/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(schedule.startTime)) {
      throw new RangeError('startTime must use the 24-hour HH:MM format');
    }

    getFormatter(schedule.timeZone);

    return {
      weekday: weekday,
      startTime: schedule.startTime,
      durationMinutes: validateDuration(schedule.durationMinutes),
      timeZone: schedule.timeZone
    };
  }

  function getWeeklyTimerState(schedule, nowMs) {
    var normalized = validateSchedule(schedule);
    var durationMinutes = normalized.durationMinutes;
    var timeParts = normalized.startTime.split(':').map(Number);
    var nowLocal = getZonedParts(nowMs, normalized.timeZone);
    var localDate = {
      year: nowLocal.year,
      month: nowLocal.month,
      day: nowLocal.day
    };
    var localWeekday = new Date(
      Date.UTC(localDate.year, localDate.month - 1, localDate.day)
    ).getUTCDay();
    var durationMs = durationMinutes * 60 * 1000;
    var thisWeekDate = addLocalDays(
      localDate,
      normalized.weekday - localWeekday
    );
    var occurrenceStarts = [-7, 0, 7].map(function (days) {
      var occurrenceDate = addLocalDays(thisWeekDate, days);

      return localDateTimeToUtc(
        {
          year: occurrenceDate.year,
          month: occurrenceDate.month,
          day: occurrenceDate.day,
          hour: timeParts[0],
          minute: timeParts[1]
        },
        normalized.timeZone
      );
    });
    var activeStartMs = occurrenceStarts
      .filter(function (startMs) {
        return nowMs >= startMs && nowMs < startMs + durationMs;
      })
      .sort(function (left, right) {
        return right - left;
      })[0];

    if (activeStartMs !== undefined) {
      var activeEndMs = activeStartMs + durationMs;

      return {
        phase: 'active',
        windowStartMs: activeStartMs,
        windowEndMs: activeEndMs,
        targetMs: activeEndMs,
        remainingMs: activeEndMs - nowMs
      };
    }

    var windowStartMs = occurrenceStarts
      .filter(function (startMs) {
        return startMs > nowMs;
      })
      .sort(function (left, right) {
        return left - right;
      })[0];

    if (windowStartMs === undefined) {
      var followingDate = addLocalDays(thisWeekDate, 14);
      windowStartMs = localDateTimeToUtc(
        {
          year: followingDate.year,
          month: followingDate.month,
          day: followingDate.day,
          hour: timeParts[0],
          minute: timeParts[1]
        },
        normalized.timeZone
      );
    }

    var windowEndMs = windowStartMs + durationMs;

    return {
      phase: 'upcoming',
      windowStartMs: windowStartMs,
      windowEndMs: windowEndMs,
      targetMs: windowStartMs,
      remainingMs: windowStartMs - nowMs
    };
  }

  function getCountdownParts(remainingMs, includeDays) {
    var totalSeconds = Math.floor(
      Math.max(0, Number.isFinite(Number(remainingMs)) ? Number(remainingMs) : 0) /
        1000
    );
    var days = includeDays ? Math.floor(totalSeconds / 86400) : 0;
    var hours = includeDays
      ? Math.floor((totalSeconds % 86400) / 3600)
      : Math.floor(totalSeconds / 3600);

    return {
      days: days,
      hours: hours,
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60
    };
  }

  function renderSectionState(sectionRoot, config, state) {
    if (config.countdownEnabled === false) {
      sectionRoot.querySelector('[data-vmax-flash-sale-upcoming]').hidden = false;
      sectionRoot.querySelector('[data-vmax-flash-sale-active]').hidden = true;
      sectionRoot.querySelector('[data-vmax-flash-sale-countdown]').hidden = true;
      sectionRoot.setAttribute('data-vmax-flash-sale-state', 'coming-soon');
      sectionRoot.classList.add('vmax-flash-sale--ready');
      return;
    }
    var parts = getCountdownParts(state.remainingMs, config.showDays);
    var showSeconds = config.showSeconds !== false;
    var upcomingMessage = sectionRoot.querySelector(
      '[data-vmax-flash-sale-upcoming]'
    );
    var activeMessage = sectionRoot.querySelector(
      '[data-vmax-flash-sale-active]'
    );
    var countdown = sectionRoot.querySelector(
      '[data-vmax-flash-sale-countdown]'
    );
    var values = {
      days: parts.days,
      hours: parts.hours,
      minutes: parts.minutes,
      seconds: parts.seconds
    };

    Object.keys(values).forEach(function (unit) {
      var node = sectionRoot.querySelector(
        '[data-vmax-flash-sale-' + unit + ']'
      );

      if (node) {
        node.textContent = String(values[unit]).padStart(2, '0');
      }
    });

    upcomingMessage.hidden = state.phase !== 'upcoming';
    activeMessage.hidden = state.phase !== 'active';
    countdown.hidden = false;
    countdown.setAttribute('aria-live', 'off');

    var accessibleParts = [];

    if (config.showDays) {
      accessibleParts.push(parts.days + ' ' + config.dayLabel);
    }

    accessibleParts.push(parts.hours + ' ' + config.hourLabel);
    accessibleParts.push(parts.minutes + ' ' + config.minuteLabel);

    if (showSeconds) {
      accessibleParts.push(parts.seconds + ' ' + config.secondLabel);
    }

    countdown.setAttribute('aria-label', accessibleParts.join(', '));
    sectionRoot.setAttribute('data-vmax-flash-sale-state', state.phase);
    sectionRoot.classList.add('vmax-flash-sale--ready');
  }

  function createWeeklyTicker(schedule, clock, onTick) {
    var disposed = false;
    var timerId = null;

    function tick() {
      timerId = null;

      if (disposed) {
        return;
      }

      var currentTime = clock.now();
      onTick(getWeeklyTimerState(schedule, currentTime));

      if (disposed) {
        return;
      }

      var elapsedInSecond = ((currentTime % 1000) + 1000) % 1000;
      var delay = elapsedInSecond === 0 ? 1000 : 1000 - elapsedInSecond;
      timerId = clock.setTimeout(tick, delay);
    }

    tick();

    return {
      dispose: function () {
        if (disposed) {
          return;
        }

        disposed = true;

        if (timerId !== null) {
          clock.clearTimeout(timerId);
          timerId = null;
        }
      }
    };
  }

  function sourcePlacementIndex(record) {
    return Array.prototype.indexOf.call(
      record.legacyContainer.children,
      record.wrapper
    );
  }

  function detachNode(node) {
    if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
  }

  function findMobileSection(documentRoot) {
    var mobileHost = documentRoot && documentRoot.querySelector
      ? documentRoot.querySelector('.vmax-mobile-home')
      : null;

    return mobileHost && mobileHost.closest
      ? mobileHost.closest('.shopify-section')
      : null;
  }

  function syncResponsivePlacements(documentRoot) {
    var documentRecords = [];

    responsivePlacements.forEach(function (record) {
      if (record.documentRoot === documentRoot) {
        documentRecords.push(record);
      }
    });

    documentRecords.forEach(function (record) {
      record.mobileSection = findMobileSection(documentRoot);

      if (
        !record.media.matches ||
        !record.mobileSection ||
        !record.mobileSection.parentNode
      ) {
        detachNode(record.replicaHost);
      }
    });

    documentRecords
      .filter(function (record) {
        return (
          record.media.matches &&
          record.mobileSection &&
          record.mobileSection.parentNode
        );
      })
      .sort(function (left, right) {
        if (left.legacyContainer === right.legacyContainer) {
          return sourcePlacementIndex(left) - sourcePlacementIndex(right);
        }

        if (left.wrapper.compareDocumentPosition) {
          var position = left.wrapper.compareDocumentPosition(right.wrapper);

          if (position & 4) {
            return -1;
          }

          if (position & 2) {
            return 1;
          }
        }

        return 0;
      })
      .forEach(function (record) {
        record.mobileSection.parentNode.insertBefore(
          record.replicaHost,
          record.mobileSection
        );
      });
  }

  function createResponsivePlacement(sectionRoot, environment) {
    var noop = {
      dispose: function () {},
      render: function () {}
    };

    if (!environment || typeof environment.matchMedia !== 'function') {
      return noop;
    }

    var wrapper = sectionRoot.closest
      ? sectionRoot.closest('.shopify-section')
      : null;
    var legacyContainer = wrapper && wrapper.closest
      ? wrapper.closest('.vmax-home-legacy-content')
      : null;
    var documentRoot = sectionRoot.ownerDocument;
    var sourceHost = sectionRoot.parentNode;
    var mobileSection = findMobileSection(documentRoot);

    if (
      !wrapper ||
      !legacyContainer ||
      !sourceHost ||
      !sourceHost.cloneNode ||
      !mobileSection ||
      !mobileSection.parentNode
    ) {
      return noop;
    }

    var replicaHost = sourceHost.cloneNode(true);
    var replicaRoot = replicaHost.querySelector
      ? replicaHost.querySelector('[data-vmax-flash-sale]')
      : null;

    if (!replicaRoot) {
      return noop;
    }

    replicaRoot.removeAttribute('data-vmax-flash-sale');
    replicaRoot.setAttribute('data-vmax-flash-sale-replica', '');

    var media = environment.matchMedia('(max-width: 990px)');
    var disposed = false;
    var record = {
      documentRoot: documentRoot,
      legacyContainer: legacyContainer,
      media: media,
      mobileSection: mobileSection,
      replicaHost: replicaHost,
      wrapper: wrapper
    };

    function sync() {
      if (disposed) {
        return;
      }

      syncResponsivePlacements(documentRoot);
    }

    if (media.addEventListener) {
      media.addEventListener('change', sync);
    } else if (media.addListener) {
      media.addListener(sync);
    }

    responsivePlacements.add(record);
    syncResponsivePlacements(documentRoot);

    return {
      dispose: function () {
        if (disposed) {
          return;
        }

        disposed = true;

        if (media.removeEventListener) {
          media.removeEventListener('change', sync);
        } else if (media.removeListener) {
          media.removeListener(sync);
        }

        responsivePlacements.delete(record);
        detachNode(replicaHost);
        syncResponsivePlacements(documentRoot);
      },
      render: function (config, state) {
        renderSectionState(replicaRoot, config, state);
      }
    };
  }

  function initializeSection(sectionRoot, clock, environment) {
    var previousInstance = sectionInstances.get(sectionRoot);

    if (previousInstance) {
      previousInstance.dispose();
    }

    var configNode = sectionRoot.querySelector(
      '[data-vmax-flash-sale-config]'
    );
    var config = JSON.parse(configNode.textContent);
    var schedule = config.countdownEnabled === false ? null : validateSchedule({
      weekday: config.weekday,
      startTime: config.startTime,
      durationMinutes: config.durationMinutes,
      timeZone: config.timeZone
    });
    var placement = createResponsivePlacement(sectionRoot, environment);
    var ticker;

    try {
      if (config.countdownEnabled === false) {
        renderSectionState(sectionRoot, config, null);
        placement.render(config, null);
        ticker = { dispose: function () {} };
      } else {
      ticker = createWeeklyTicker(
        schedule,
        clock,
        function (state) {
          renderSectionState(sectionRoot, config, state);
          placement.render(config, state);
        }
      );
      }
    } catch (error) {
      placement.dispose();
      throw error;
    }
    var disposed = false;
    var instance = {
      dispose: function (options) {
        if (disposed) {
          return;
        }

        disposed = true;
        ticker.dispose();
        placement.dispose();

        if (sectionInstances.get(sectionRoot) === instance) {
          sectionInstances.delete(sectionRoot);
        }
      }
    };

    sectionInstances.set(sectionRoot, instance);

    return instance;
  }

  function findSections(scope) {
    var sections = [];

    if (scope.matches && scope.matches('[data-vmax-flash-sale]')) {
      sections.push(scope);
    }

    if (scope.querySelectorAll) {
      Array.prototype.push.apply(
        sections,
        scope.querySelectorAll('[data-vmax-flash-sale]')
      );
    }

    return sections;
  }

  function createBrowserClock(environment) {
    return {
      now: function () {
        return environment.Date.now();
      },
      setTimeout: environment.setTimeout.bind(environment),
      clearTimeout: environment.clearTimeout.bind(environment)
    };
  }

  function markInvalid(sectionRoot, environment) {
    var warning = sectionRoot.querySelector(
      '[data-vmax-flash-sale-config-error]'
    );

    sectionRoot.setAttribute('data-vmax-flash-sale-state', 'invalid');

    if (
      warning &&
      environment.Shopify &&
      environment.Shopify.designMode
    ) {
      warning.hidden = false;
    }
  }

  function initializeScope(scope, environment) {
    var clock = createBrowserClock(environment);

    findSections(scope).forEach(function (sectionRoot) {
      try {
        initializeSection(sectionRoot, clock, environment);
      } catch (error) {
        markInvalid(sectionRoot, environment);
      }
    });
  }

  function disposeScope(scope) {
    findSections(scope).forEach(function (sectionRoot) {
      var instance = sectionInstances.get(sectionRoot);

      if (instance) {
        instance.dispose();
      }
    });
  }

  function boot(documentRoot, environment) {
    if (bootedDocuments.has(documentRoot)) {
      return;
    }

    bootedDocuments.add(documentRoot);
    initializeScope(documentRoot, environment);
    documentRoot.addEventListener('shopify:section:load', function (event) {
      initializeScope(event.target, environment);
    });
    documentRoot.addEventListener('shopify:section:unload', function (event) {
      disposeScope(event.target);
    });
    documentRoot.addEventListener('shopify:section:reorder', function () {
      syncResponsivePlacements(documentRoot);
    });
  }

  return {
    boot: boot,
    createResponsivePlacement: createResponsivePlacement,
    createWeeklyTicker: createWeeklyTicker,
    getCountdownParts: getCountdownParts,
    getWeeklyTimerState: getWeeklyTimerState,
    initializeSection: initializeSection,
    renderSectionState: renderSectionState,
    syncResponsivePlacements: syncResponsivePlacements
  };
});
